import {
  ADDITION,
  Brush,
  Evaluator,
  INTERSECTION,
  SUBTRACTION
} from 'three-bvh-csg';
import { BoxGeometry } from 'three/src/geometries/BoxGeometry';
import { ConeGeometry } from 'three/src/geometries/ConeGeometry';
import { MeshBasicMaterial } from 'three/src/materials/MeshBasicMaterial';
import { BufferAttribute } from 'three/src/core/BufferAttribute';
import { Box3 } from 'three/src/math/Box3';
import { Vector3 } from 'three/src/math/Vector3';
import { Mesh } from 'three/src/objects/Mesh';
import { resetCoords } from './positioning';
import { getGlobalMinY } from './stopeGeometry';
import { BeachSurface, pruneDominatedSurfaces } from './fillSurface';

/**
 * Display solids for the stope fill. These are ILLUSTRATIVE only - every reported volume comes from the
 * exact integral in fillSurface, never from CSG. CSG on real (coarsely tessellated) stope geometry reads
 * roughly +0.6% to +1.5% high even on a cleanly repaired mesh, which is fine for a shell you look at and
 * not fine for a number an engineer plans against.
 *
 * The fill is the UNION of every pour's cone, matching the pointwise-max surface the integral measures.
 */

const RADIAL_SEGMENTS = 32;
const HEIGHT_SEGMENTS = 2;

// Deliberately plain. A cutter is never rendered, but anything eye-catching here becomes the material of
// the entire beach surface the moment groups come back on - see the note on useGroups in `evaluate`.
const CUTTER_MATERIAL = new MeshBasicMaterial();

const geometryBoundingBox = (mesh: Mesh): Box3 => {
  mesh.geometry.computeBoundingBox();
  return mesh.geometry.boundingBox!;
};

// A cone of repose is degenerate at 0 degrees (infinite radius), so a level fill uses a wide flat-topped
// box instead. This is purely a modelling detail of the DISPLAY solid - the volume integral treats
// theta = 0 as an ordinary horizontal plane and needs no special case at all.
export const isFlatFill = (slopeDegrees: number): boolean => slopeDegrees <= 0;

/**
 * The floor a cutter's flat base should sit at. Dropping it below the stope keeps the cap from landing
 * near-coplanar with the tilted, coarsely triangulated stope floor, which makes the CSG boolean unstable
 * and leaves triangular "teeth" along the bottom. The apex height and the beach slope are unaffected -
 * the cone simply grows taller and wider below the solid.
 */
export const cutterFloorY = (stope: Mesh, level: number): number => Math.min(level, getGlobalMinY(stope) - 1);

/**
 * A box spanning the whole stope cross-section from floorY up to topY. Intersected with the stope it
 * yields a level fill with a horizontal top, used for rock fill and any 0-degree beach.
 */
export const createFlatFillSolid = (stope: Mesh, topY: number, floorY: number): Mesh => {
  const box = geometryBoundingBox(stope);
  const size = box.getSize(new Vector3());
  const centre = box.getCenter(new Vector3());

  const margin = Math.max(size.x, size.z) * 0.1 + 1;
  const boxHeight = topY - floorY;
  const fillMesh = new Mesh(
    new BoxGeometry(size.x + margin * 2, boxHeight, size.z + margin * 2),
    CUTTER_MATERIAL,
  );

  fillMesh.position.set(centre.x, floorY + boxHeight / 2, centre.z);
  resetCoords(fillMesh);

  return fillMesh;
};

/**
 * The solid bounded above by one pour's beach: a cone with its apex at (anchor.x, level, anchor.z) and
 * its base below the stope, or a flat-topped box when the slope is 0.
 *
 * RADIUS: `(level - floorY) / tan(slopeDegrees)`. slopeDegrees is the surface slope FROM HORIZONTAL, so
 * the lateral face drops by exactly `rho * tan(slopeDegrees)` over a horizontal run of rho - which is
 * precisely the surface `level - rho * tan(theta)` that fillSurface integrates.
 *
 * This replaces an older convention where the caller passed a "top angle" and the code did
 * `topAngle = 180 - pasteAngle; radius = height * tan(topAngle / 2)`. That yields a surface slope of
 * pasteAngle / 2, i.e. HALF what the setting claimed - the hardcoded 2.5 in the old code was really a
 * 1.25 degree beach. Do not reintroduce the half-angle: slopeDegrees now means exactly what it says.
 */
export const makeSurfaceCutter = (stope: Mesh, surface: BeachSurface): Mesh => {
  const floorY = cutterFloorY(stope, surface.level);
  if (isFlatFill(surface.slopeDegrees)) {
    return createFlatFillSolid(stope, surface.level, floorY);
  }

  const height = surface.level - floorY;
  const radius = height / Math.tan((surface.slopeDegrees * Math.PI) / 180);
  const cone = new Mesh(
    new ConeGeometry(radius, height, RADIAL_SEGMENTS, HEIGHT_SEGMENTS),
    CUTTER_MATERIAL,
  );

  cone.position.set(surface.anchor.x, floorY + height / 2, surface.anchor.z);
  resetCoords(cone);

  return cone;
};

const evaluate = (left: Mesh, right: Mesh, operation: number): Brush => {
  // three-bvh-csg requires matching attribute sets on both operands.
  left.geometry.setAttribute('uv', new BufferAttribute(new Float32Array([]), 1));
  left.geometry.computeVertexNormals();
  right.geometry.computeVertexNormals();

  const leftBrush = new Brush(left.geometry, left.material);
  const rightBrush = new Brush(right.geometry, right.material);
  leftBrush.updateMatrixWorld();
  rightBrush.updateMatrixWorld();

  const evaluator = new Evaluator();
  // Groups OFF. With them on the result gets a material ARRAY [stope, cutter] plus geometry groups, so
  // every face the boolean took from the cutter - the entire beach surface - renders with the cutter's
  // material. Off, the result inherits the stope's material, which is what both consumers want: the
  // unfilled shell is drawn exactly like the stope, and a pour shell has its own colour attached over it.
  evaluator.useGroups = false;

  return evaluator.evaluate(leftBrush, rightBrush, operation);
};

/**
 * One solid covering everything under ALL of `surfaces` - the union of their cutters. Null when there are
 * none, meaning nothing is filled.
 *
 * Unioning the CUTTERS rather than the already-intersected fills is deliberate: cutters are smooth
 * primitives, so the union is a clean boolean, and the coarse stope geometry is only ever cut once at the
 * end. It also keeps the display consistent with the integral, which takes the same pointwise maximum.
 */
export const buildCutterUnion = (stope: Mesh, surfaces: readonly BeachSurface[]): Mesh | null => {
  // Pruning first is what keeps this affordable - see pruneDominatedSurfaces. Unioning the concentric
  // cones an unmoved pipe produces is pathological for CSG, and each union feeds the next.
  const visible = pruneDominatedSurfaces(surfaces);
  if (visible.length === 0) return null;

  return visible.reduce<Mesh | null>((combined, surface) => {
    const cutter = makeSurfaceCutter(stope, surface);
    return combined ? (evaluate(combined, cutter, ADDITION) as unknown as Mesh) : cutter;
  }, null);
};

/** The filled solid: everything in the stope below the fill surface. */
export const buildFilledSolid = (pristineStope: Mesh, surfaces: readonly BeachSurface[]): Brush | null => {
  const cutter = buildCutterUnion(pristineStope, surfaces);
  return cutter ? evaluate(pristineStope, cutter, INTERSECTION) : null;
};

/**
 * The void left above the fill: the stope minus everything below the fill surface. Display only, and
 * again cut from the pristine mesh rather than accumulated.
 */
export const buildUnfilledSolid = (pristineStope: Mesh, surfaces: readonly BeachSurface[]): Brush => {
  const cutter = buildCutterUnion(pristineStope, surfaces);
  return cutter
    ? evaluate(pristineStope, cutter, SUBTRACTION)
    : (new Brush(pristineStope.geometry, pristineStope.material) as Brush);
};

/**
 * The display shell for one pour: what its cone adds on top of everything already placed.
 *
 * `previous` must be the surfaces placed BEFORE this pour and `surface` this pour's own - the shell is
 * the difference, so passing the full list as `previous` yields nothing. Cut from the pristine stope, so
 * a stack of ten pours is ten independent booleans rather than ten chained ones.
 */
export const buildSliceSolid = (
  pristineStope: Mesh,
  previous: readonly BeachSurface[],
  surface: BeachSurface,
): Brush => {
  const filled = evaluate(pristineStope, makeSurfaceCutter(pristineStope, surface), INTERSECTION);
  const under = buildCutterUnion(pristineStope, previous);
  return under ? evaluate(filled as unknown as Mesh, under, SUBTRACTION) : filled;
};
